import React from 'react';
import { Box, Container, Grid, Typography, Paper } from '@mui/material';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import LockIcon from '@mui/icons-material/Lock';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CampaignIcon from '@mui/icons-material/Campaign';
import { motion } from 'framer-motion';

const cardColors = [
  "rgba(227, 242, 253, 0.5)", // Light Blue
  "rgba(232, 245, 233, 0.5)", // Light Green
  "rgba(252, 228, 236, 0.5)", // Light Pink
  "rgba(255, 243, 224, 0.5)", // Light Orange
  "rgba(237, 231, 246, 0.5)", // Light Purple
  "rgba(224, 247, 250, 0.5)", // Light Cyan
];

const features = [
  {
    icon: <TrendingUpIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
    title: 'Supercharge Your Sales Pipeline',
    description:
      'Send personalised recommendations to your potential customers based on their behaviour, purchase history and more.',
  },
  {
    icon: <SmartToyIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
    title: 'Streamline Your Process Efficiency',
    description:
      'As our bots handle routine tasks, your team gets more time to focus on larger, strategic initiatives that need a human touch.',
  },
  {
    icon: <CampaignIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
    title: 'Implement Savvy Marketing Tactics',
    description:
      'Establish a custom WhatsApp drip campaign to reach your dream 100 customers just when they least anticipate it.',
  },
  {
    icon: <ThumbUpAltIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
    title: 'Enhance Every Customer Interaction',
    description:
      'Keep your contacts engaged by dispatching timely updates, reminders and even a dash of amusing GIFs for good measure.',
  },
  {
    icon: <LockIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
    title: 'Establish a Single Source of Truth',
    description:
      'Maintain clear communication channels by storing all conversations neatly collated in a single location, ensuring nothing is overlooked.',
  },
  {
    icon: <AttachMoneyIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
    title: 'Amplify Your Profits and Revenue',
    description:
      'Construct and execute tailored Facebook and Instagram advertisements that guide leads directly to WhatsApp, consequently reducing your cost per lead.',
  },
];

const CustomerSection = () => {
  return (
    <Box
      py={8}
      sx={{
        backgroundImage: `url('bg2.svg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h3"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          Why <span style={{ color: '#1976d2' }}>1500+ Customers</span> Trust VertexSuite?
        </Typography>

        <Typography
          variant="body1"
          align="left"
          paragraph
          color="text.secondary"
          sx={{
            fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
            mb: 5,
            mx: { xs: 2, sm: 10, md: 16 },
          }}
        >
          VertexSuite is a potent tool to generate WhatsApp QR codes and an essential platform to boost your WhatsApp Automation requirements. Below are five compelling reasons why you should consider Gallabox for all your WhatsApp Automation needs:
        </Typography>

        <Grid container spacing={4} justifyContent="center" sx={{ mt: 2 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Paper
                  elevation={3}
                  sx={{
                    borderRadius: 3,
                    p: 4,
                    textAlign: 'center',
                    minHeight: '300px', // Set consistent minimum height
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    backgroundColor: cardColors[index % cardColors.length],
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)',
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ 
                      fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' }, 
                      color: 'text.secondary',
                      flexGrow: 1, // Allow description to take available space
                    }}
                  >
                    {feature.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default CustomerSection;